import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Skeleton,
  Alert,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import StarsOutlinedIcon from "@mui/icons-material/StarsOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import { toast, ToastContainer } from "react-toastify";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import { listCourses } from "../../api/assessmentHierarchy";
import {
  apiErrorMessage,
  TEST_DIFFICULTIES,
  DIFFICULTY_COLORS,
  listTestTypes,
  getTestDetail,
  createTest,
  updateTest,
  publishTest,
  previewTest,
  getTestWeightage,
} from "../../api/assessmentTests";
import TestDistributionBuilder, { EMPTY_DISTRIBUTION } from "./TestDistributionBuilder";
import DifficultySplitCard from "./DifficultySplitCard";
import { canRotateQuestions } from "./practiceRotation";
import TestWeightageCard from "./TestWeightageCard";
import AssessmentHeader from "./shell/AssessmentHeader";
import { cardSx, SURFACE } from "./shell/assessmentTheme";

const ACCENT = "#17479e";
const FIELD_SX = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
const REQUIRED_MESSAGE = "Please fill out this field";
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const STATUS_CFG = {
  DRAFT: { label: "Draft", color: "#b26a00", bg: "#fff4e0" },
  PUBLISHED: { label: "Published", color: "#2e7d32", bg: "#e8f5e9" },
  ARCHIVED: { label: "Archived", color: "#555", bg: "#f0f0f0" },
};

const DISTRIBUTION_HEADINGS = {
  SUBJECT: "Distribution by subject",
  CHAPTER: "Distribution by chapter",
  TOPIC: "Distribution by topic",
  TOPIC_SUBTOPIC: "Distribution by topic / sub-topic",
};

const labelOf = (options, value) => options.find((o) => o.value === value)?.label || value;

const isBlank = (v) => v === "" || v === null || v === undefined;
const isPositive = (v) => !isBlank(v) && Number(v) > 0;
const isPositiveInt = (v) => isPositive(v) && Number.isInteger(Number(v));
const isNonNegative = (v) => !isBlank(v) && Number(v) >= 0;

function SectionHeader({ icon, title, subtitle, action }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        background: "#f7f9fd",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
          boxShadow: `0 4px 10px ${ACCENT}40`,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a2e" }}>{title}</Typography>
        {subtitle && (
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>{subtitle}</Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

function StatTile({ icon, label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        p: 1.5,
        borderRadius: 2,
        backgroundColor: "#f5f7fb",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: `${ACCENT}1a`,
          color: ACCENT,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a2e", lineHeight: 1.2 }} noWrap>
          {value}
        </Typography>
        <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>{label}</Typography>
      </Box>
    </Box>
  );
}

function StatusChip({ value }) {
  const cfg = STATUS_CFG[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

function FormSkeleton() {
  return (
    <Card sx={{ ...cardSx, p: 2.5 }}>
      <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={40} sx={{ mb: 2, borderRadius: 2 }} />
      ))}
    </Card>
  );
}

function ConfirmDialog({ open, title, message, confirmText, loading, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} {...BUTTON_STYLES.secondary}>
          Cancel
        </Button>
        <LoadingButton variant="contained" loading={loading} onClick={onConfirm} {...BUTTON_STYLES.primary}>
          {confirmText}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function AssessmentTestForm() {
  const navigate = useNavigate();
  const { testId } = useParams();
  const isEdit = Boolean(testId);

  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [testTypes, setTestTypes] = useState([]);

  const [status, setStatus] = useState("DRAFT");
  const [courseId, setCourseId] = useState("");
  const [testTypeId, setTestTypeId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [instructionsText, setInstructionsText] = useState("");
  const [totalQuestions, setTotalQuestions] = useState("");
  const [marksPerQuestion, setMarksPerQuestion] = useState("");
  const [negativeMarks, setNegativeMarks] = useState("0");
  const [requiresPremiumPlan, setRequiresPremiumPlan] = useState(false);
  const [rotateQuestions, setRotateQuestions] = useState(false);
  const [examMonth, setExamMonth] = useState("");
  const [examYear, setExamYear] = useState("");
  const [displayOrder, setDisplayOrder] = useState("");
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [distribution, setDistribution] = useState(EMPTY_DISTRIBUTION);
  // The uploaded weightage the test is built from, if any ({ rows, totalQuestions, pyqQuestions, newQuestions }).
  const [weightage, setWeightage] = useState(null);
  // How many distribution items the saved test has; 0 means a test saved without a distribution.
  const [savedItemCount, setSavedItemCount] = useState(0);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [snapshotTick, setSnapshotTick] = useState(0);
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const isDraft = status === "DRAFT";
  const touch = (key) => setTouched((prev) => ({ ...prev, [key]: true }));

  const applyTest = useCallback((test) => {
    setStatus(test.status || "DRAFT");
    setCourseId(test.courseId ?? "");
    setTestTypeId(test.testTypeId ?? "");
    setName(test.name || "");
    setDescription(test.description || "");
    setDurationMinutes(test.durationMinutes ?? "");
    setInstructionsText(test.instructionsText || "");
    setTotalQuestions(test.totalQuestions ?? "");
    setMarksPerQuestion(test.marksPerQuestion ?? "");
    setNegativeMarks(test.negativeMarksPerQuestion ?? "0");
    setRequiresPremiumPlan(Boolean(test.requiresPremiumPlan));
    setRotateQuestions(Boolean(test.rotateQuestions));
    setExamMonth(test.examMonth ?? "");
    setExamYear(test.examYear ?? "");
    setDisplayOrder(test.displayOrder ?? "");
  }, []);

  const applyDistribution = useCallback((detail) => {
    const saved = detail.distribution;
    const items = (saved?.items || []).map((i) => ({
      subjectId: i.subjectId,
      chapterId: i.chapterId,
      topicId: i.topicId ?? null,
      subTopicId: i.subTopicId ?? null,
      questionCount: i.questionCount,
    }));
    setDistribution({ items, difficultyCounts: saved?.difficultyCounts || {} });
    setSavedItemCount(items.length);
  }, []);

  const loadWeightage = useCallback(
    (id) =>
      getTestWeightage(id)
        .then((data) => setWeightage(data))
        .catch(() => setWeightage(null)),
    [],
  );

  const loadPreview = useCallback((id) => {
    setPreviewLoading(true);
    setPreviewError("");
    return previewTest(id)
      .then((data) => setPreview(data))
      .catch((err) => {
        setPreview(null);
        setPreviewError(apiErrorMessage(err, "Failed to load preview"));
      })
      .finally(() => setPreviewLoading(false));
  }, []);

  const loadDetail = useCallback(
    (id) =>
      getTestDetail(id).then((detail) => {
        applyTest(detail.test);
        applyDistribution(detail);
        setResolvedCount((detail.resolvedQuestions || []).length);
        // Same batch as the state above: the effect below snapshots the freshly loaded values.
        setSnapshotTick((t) => t + 1);
        return detail;
      }),
    [applyTest, applyDistribution],
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([
      listCourses({ includeInactive: true }),
      // All non-deleted types: a test's own type may have been deactivated since it was created.
      listTestTypes(),
    ])
      .then(([courseRows, typeRows]) => {
        setCourses(courseRows);
        setTestTypes(typeRows || []);
        if (!isEdit) {
          setStatus("DRAFT");
          setDistribution(EMPTY_DISTRIBUTION);
          setPreview(null);
          return null;
        }
        return loadDetail(testId)
          .then(() => loadWeightage(testId))
          .then(() => loadPreview(testId));
      })
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load test")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, testId]);

  const currentType = testTypes.find((t) => t.id === testTypeId);
  const level = currentType?.distributionLevel;
  const isPyqType = currentType?.questionUsage === "PYQ";
  const examPeriodIncomplete = isPyqType && (examMonth === "") !== (examYear === "");
  // Types on the old Subject level cannot be used with the Test Builder.
  const legacyType = level === "SUBJECT";
  const editable = !isEdit || isDraft;
  const hasWeightage = (weightage?.rows?.length || 0) > 0;
  // A test may be created (or saved) with no distribution at all - an empty draft to upload a weightage into
  // afterwards. Once a test has distribution items, clearing them all is still caught as a mistake.
  const noDistribution = distribution.items.length === 0 && savedItemCount === 0;
  // Offer active types, plus the test's current type even if it has been deactivated.
  const typeOptions = testTypes.filter((t) => t.active !== false || t.id === testTypeId);

  // Unsaved-changes tracking: compare the editable state with the snapshot taken after the last load / save.
  const currentSnapshot = useMemo(
    () =>
      JSON.stringify([
        courseId,
        testTypeId,
        name,
        description,
        durationMinutes,
        instructionsText,
        totalQuestions,
        marksPerQuestion,
        negativeMarks,
        requiresPremiumPlan,
        rotateQuestions,
        examMonth,
        examYear,
        displayOrder,
        distribution,
      ]),
    [
      courseId,
      testTypeId,
      name,
      description,
      durationMinutes,
      instructionsText,
      totalQuestions,
      marksPerQuestion,
      negativeMarks,
      requiresPremiumPlan,
      rotateQuestions,
      examMonth,
      examYear,
      displayOrder,
      distribution,
    ],
  );
  useEffect(() => {
    setSavedSnapshot(currentSnapshot);
    // Only re-snapshot when a load completes (snapshotTick), never on edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotTick]);
  const dirty = savedSnapshot !== null && savedSnapshot !== currentSnapshot;

  const errors = {
    courseId: isBlank(courseId),
    testTypeId: isBlank(testTypeId),
    name: !name.trim(),
    durationMinutes: !isPositiveInt(durationMinutes),
    totalQuestions: !isPositiveInt(totalQuestions),
    marksPerQuestion: !isPositive(marksPerQuestion),
    negativeMarks: !isNonNegative(negativeMarks),
  };
  const isValid = !Object.values(errors).some(Boolean) && !legacyType;

  const helper = (key, rawValue, invalidMessage) =>
    touched[key] && errors[key] ? (isBlank(rawValue) ? REQUIRED_MESSAGE : invalidMessage) : undefined;

  const buildPayload = () => ({
    courseId,
    testTypeId,
    name: name.trim(),
    description: description.trim() || null,
    durationMinutes: Number(durationMinutes),
    instructionsText: instructionsText.trim() || null,
    requiresPremiumPlan,
    rotateQuestions: canRotateQuestions(currentType, true) && rotateQuestions,
    // PYQ test types only: draw questions only from this exam month and year (both or neither)
    examMonth: isPyqType && examMonth !== "" && examYear !== "" ? Number(examMonth) : null,
    examYear: isPyqType && examMonth !== "" && examYear !== "" ? Number(examYear) : null,
    displayOrder: isBlank(displayOrder) ? undefined : Number(displayOrder),
    totalQuestions: Number(totalQuestions),
    marksPerQuestion: Number(marksPerQuestion),
    negativeMarksPerQuestion: Number(negativeMarks),
  });

  const buildDistributionPayload = () => ({
    items: distribution.items.map((i) => ({
      subjectId: i.subjectId,
      chapterId: i.chapterId,
      topicId: i.topicId ?? null,
      subTopicId: i.subTopicId ?? null,
      questionCount: Number(i.questionCount),
    })),
    difficultyCounts: Object.fromEntries(
      TEST_DIFFICULTIES.map((d) => [d.value, Number(distribution.difficultyCounts?.[d.value]) || 0]),
    ),
  });

  const distributionProblem = () => {
    const total = Number(totalQuestions);
    if (distribution.items.length === 0) return "Pick at least one item for the distribution.";
    const counts = TEST_DIFFICULTIES.map((d) => distribution.difficultyCounts?.[d.value]);
    if (counts.some((c) => !isBlank(c) && !(Number.isInteger(Number(c)) && Number(c) >= 0))) {
      return "Each difficulty count must be a whole number, 0 or more.";
    }
    if (distribution.items.some((i) => !isPositiveInt(i.questionCount))) {
      return "Every picked item needs a question count greater than 0.";
    }
    const pickSum = distribution.items.reduce((s, i) => s + Number(i.questionCount), 0);
    if (pickSum !== total) return `Picked questions (${pickSum}) must equal Total Questions (${total}).`;
    const difficultySum = TEST_DIFFICULTIES.reduce(
      (s, d) => s + (Number(distribution.difficultyCounts?.[d.value]) || 0),
      0,
    );
    if (difficultySum !== total) return `Difficulty total (${difficultySum}) must equal Total Questions (${total}).`;
    return "";
  };

  // Parent contract of TestDistributionBuilder: when the user picks another course or test type, the picked
  // items are cleared in the same state update, so no commit pairs a new type with the old items.
  const handleCourseChange = (value) => {
    setCourseId(value);
    setDistribution(EMPTY_DISTRIBUTION);
  };
  const handleTestTypeChange = (value) => {
    setTestTypeId(value);
    setDistribution(EMPTY_DISTRIBUTION);
  };

  const handleSave = () => {
    setTouched({
      courseId: true,
      testTypeId: true,
      name: true,
      durationMinutes: true,
      totalQuestions: true,
      marksPerQuestion: true,
      negativeMarks: true,
    });
    if (!isValid || submitting) return;
    // A weightage-built test keeps its sections, and a test with no distribution yet is saved as an empty draft:
    // in both cases the builder distribution is neither validated nor sent.
    let problem = "";
    if (examPeriodIncomplete) {
      problem = "Pick both the exam month and the exam year, or leave both empty.";
    } else if (hasWeightage) {
      if (Number(totalQuestions) !== weightage.totalQuestions) {
        problem = `Total Questions (${Number(totalQuestions)}) must equal the weightage total (${weightage.totalQuestions}).`;
      }
    } else if (!noDistribution) {
      problem = distributionProblem();
    }
    if (problem) {
      toast.error(problem);
      return;
    }
    const payload =
      hasWeightage || noDistribution ? buildPayload() : { ...buildPayload(), distribution: buildDistributionPayload() };
    setSubmitting(true);
    if (!isEdit) {
      createTest(payload)
        .then((created) => {
          toast.success("Test created.");
          navigate(`/assessment/tests/${created.id}/edit`, { replace: true });
        })
        .catch((err) => toast.error(apiErrorMessage(err, "Failed to create test")))
        .finally(() => setSubmitting(false));
      return;
    }
    updateTest(testId, payload)
      .then(() => loadDetail(testId))
      .then(() => loadPreview(testId))
      .then(() => toast.success("Test updated successfully!"))
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to update test")))
      .finally(() => setSubmitting(false));
  };

  // The weightage replaces (or removes) the test's sections, so reload the detail, the preview and the weightage.
  const handleWeightageChanged = () =>
    loadDetail(testId)
      .then(() => loadWeightage(testId))
      .then(() => loadPreview(testId))
      .then(() => toast.success("Weightage updated."));

  const handlePublish = () => {
    setPublishing(true);
    publishTest(testId)
      .then(() => {
        toast.success("Test published!");
        setPublishOpen(false);
        return loadDetail(testId).then(() => loadPreview(testId));
      })
      .catch((err) => {
        setPublishOpen(false);
        toast.error(apiErrorMessage(err, "Failed to publish test"), { autoClose: 10000 });
      })
      .finally(() => setPublishing(false));
  };

  const goBack = () => localStorage.setItem("assessmentTabLabel", "Tests");

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, backgroundColor: SURFACE.page, minHeight: "100vh" }}>
      <ToastContainer />

      <AssessmentHeader
        title={isEdit ? "Edit Test" : "New Test"}
        icon={<DescriptionOutlinedIcon />}
        crumbs={[
          <Link
            key="assessment"
            to="/assessment"
            onClick={goBack}
            style={{ textDecoration: "none", color: "rgba(255,255,255,0.85)", fontSize: "0.78rem" }}
          >
            Assessment
          </Link>,
          <Typography key="tests" sx={{ fontSize: "0.78rem", color: "#fff", fontWeight: 600 }}>
            Tests
          </Typography>,
        ]}
      />

      {loading ? (
        <FormSkeleton />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/*
            Layout: at md+ the two column wrappers are real flex columns (left: info + difficulty,
            right: mapping + preview). Below md the wrappers use display: contents, so the four cards become
            direct items of the grid and the CSS `order` values give: info, mapping, difficulty, preview.
          */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
              alignItems: "start",
            }}
          >
            <Box sx={{ display: { xs: "contents", md: "flex" }, flexDirection: "column", gap: 3, minWidth: 0 }}>
              <Card sx={{ ...cardSx, overflow: "hidden", order: 1 }}>
                <SectionHeader
                  icon={<DescriptionOutlinedIcon sx={{ fontSize: 20 }} />}
                  title="Test Information"
                  subtitle="Name, timing and marking scheme"
                  action={isEdit ? <StatusChip value={status} /> : null}
                />
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
                  {isEdit && !isDraft && (
                    <Alert severity="info">
                      This test is {STATUS_CFG[status]?.label.toLowerCase() || status.toLowerCase()}, so its details and
                      distribution can no longer be changed.
                    </Alert>
                  )}
                  <TextField
                    label="Test Name"
                    fullWidth
                    size="small"
                    required
                    disabled={isEdit && !isDraft}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => touch("name")}
                    error={Boolean(touched.name && errors.name)}
                    helperText={helper("name", name)}
                    sx={FIELD_SX}
                  />
                  <TextField
                    label="Description"
                    fullWidth
                    size="small"
                    disabled={isEdit && !isDraft}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={FIELD_SX}
                  />
                  <TextField
                    label="Instructions"
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    disabled={isEdit && !isDraft}
                    value={instructionsText}
                    onChange={(e) => setInstructionsText(e.target.value)}
                    sx={FIELD_SX}
                  />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2 }}>
                    <TextField
                      label="Total Questions"
                      type="number"
                      fullWidth
                      size="small"
                      required
                      disabled={isEdit && !isDraft}
                      value={totalQuestions}
                      onChange={(e) => setTotalQuestions(e.target.value)}
                      onBlur={() => touch("totalQuestions")}
                      error={Boolean(touched.totalQuestions && errors.totalQuestions)}
                      helperText={helper("totalQuestions", totalQuestions, "Must be a whole number greater than 0")}
                      sx={FIELD_SX}
                    />
                    <TextField
                      label="Duration (minutes)"
                      type="number"
                      fullWidth
                      size="small"
                      required
                      disabled={isEdit && !isDraft}
                      value={durationMinutes}
                      onChange={(e) => setDurationMinutes(e.target.value)}
                      onBlur={() => touch("durationMinutes")}
                      error={Boolean(touched.durationMinutes && errors.durationMinutes)}
                      helperText={helper("durationMinutes", durationMinutes, "Must be a whole number greater than 0")}
                      sx={FIELD_SX}
                    />
                    <TextField
                      label="Marks per Question"
                      type="number"
                      fullWidth
                      size="small"
                      required
                      disabled={isEdit && !isDraft}
                      value={marksPerQuestion}
                      onChange={(e) => setMarksPerQuestion(e.target.value)}
                      onBlur={() => touch("marksPerQuestion")}
                      error={Boolean(touched.marksPerQuestion && errors.marksPerQuestion)}
                      helperText={helper("marksPerQuestion", marksPerQuestion, "Must be greater than 0")}
                      sx={FIELD_SX}
                    />
                    <TextField
                      label="Negative Marks per Question"
                      type="number"
                      fullWidth
                      size="small"
                      required
                      disabled={isEdit && !isDraft}
                      value={negativeMarks}
                      onChange={(e) => setNegativeMarks(e.target.value)}
                      onBlur={() => touch("negativeMarks")}
                      error={Boolean(touched.negativeMarks && errors.negativeMarks)}
                      helperText={helper("negativeMarks", negativeMarks, "Must be 0 or more")}
                      sx={FIELD_SX}
                    />
                    <TextField
                      label="Display Order (optional)"
                      type="number"
                      fullWidth
                      size="small"
                      disabled={isEdit && !isDraft}
                      value={displayOrder}
                      onChange={(e) => setDisplayOrder(e.target.value)}
                      sx={FIELD_SX}
                    />
                    {isPyqType && (
                      <Box sx={{ display: "flex", gap: 1.5 }}>
                        <TextField
                          select
                          label="Exam month (optional)"
                          fullWidth
                          size="small"
                          disabled={isEdit && !isDraft}
                          value={examMonth}
                          onChange={(e) => setExamMonth(e.target.value)}
                          error={examPeriodIncomplete}
                          sx={FIELD_SX}
                        >
                          <MenuItem value="">Any month</MenuItem>
                          {MONTH_NAMES.map((m, i) => (
                            <MenuItem key={m} value={i + 1}>
                              {m}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label="Exam year"
                          type="number"
                          fullWidth
                          size="small"
                          disabled={isEdit && !isDraft}
                          value={examYear}
                          onChange={(e) => setExamYear(e.target.value)}
                          error={examPeriodIncomplete}
                          helperText={
                            examPeriodIncomplete
                              ? "Pick both the month and the year"
                              : "Only questions from this exam month & year are used"
                          }
                          sx={FIELD_SX}
                        />
                      </Box>
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={requiresPremiumPlan}
                          disabled={isEdit && !isDraft}
                          onChange={(e) => setRequiresPremiumPlan(e.target.checked)}
                        />
                      }
                      label="Requires Premium Plan"
                    />
                    {canRotateQuestions(currentType, !isEdit || isDraft) && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={rotateQuestions}
                            onChange={(e) => setRotateQuestions(e.target.checked)}
                          />
                        }
                        label="Rotate questions each attempt"
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ ...cardSx, overflow: "hidden", order: 3 }}>
                <SectionHeader
                  icon={<BarChartOutlinedIcon sx={{ fontSize: 20 }} />}
                  title="Difficulty Distribution"
                  subtitle="How hard the questions are"
                />
                <CardContent>
                  <DifficultySplitCard
                    totalQuestions={totalQuestions}
                    value={distribution.difficultyCounts}
                    onChange={(counts) => setDistribution({ ...distribution, difficultyCounts: counts })}
                    disabled={(isEdit && !isDraft) || hasWeightage}
                  />
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ display: { xs: "contents", md: "flex" }, flexDirection: "column", gap: 3, minWidth: 0 }}>
              <Card sx={{ ...cardSx, overflow: "hidden", order: 3 }}>
                <SectionHeader
                  icon={<ListAltOutlinedIcon sx={{ fontSize: 20 }} />}
                  title="Weightage"
                  subtitle="Upload weightage file to build this test from exactly those counts/Use the distribution above"
                />
                <CardContent>
                  <TestWeightageCard
                    testId={isEdit ? testId : null}
                    weightage={weightage}
                    editable={isEdit && isDraft}
                    onChanged={handleWeightageChanged}
                  />
                </CardContent>
              </Card>

              <Card sx={{ ...cardSx, overflow: "hidden", order: 2 }}>
                <SectionHeader
                  icon={<AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />}
                  title="Exam & Mapping"
                  subtitle="Exam, test type and where the questions come from"
                />
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {(!courseId || !testTypeId) && !legacyType && (
                    <Typography variant="body2" color="text.secondary">
                      Choose an exam and a test type to set up the distribution.
                    </Typography>
                  )}
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <FormControl fullWidth size="small" required disabled={isEdit}>
                      <InputLabel>Exam</InputLabel>
                      <Select
                        label="Exam *"
                        value={courseId}
                        onChange={(e) => handleCourseChange(e.target.value)}
                        onClose={() => touch("courseId")}
                      >
                        {courses.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" required disabled={isEdit && !isDraft}>
                      <InputLabel>Test Type</InputLabel>
                      <Select
                        label="Test Type *"
                        value={testTypeId}
                        onChange={(e) => handleTestTypeChange(e.target.value)}
                        onClose={() => touch("testTypeId")}
                      >
                        {typeOptions.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.active === false ? `${t.name} (inactive)` : t.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {legacyType && editable && (
                        <FormHelperText error>
                          This test type uses the legacy Subject level and cannot be used with the Test Builder. Choose
                          another type.
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                  {isEdit && !isDraft && (
                    <Typography variant="body2" color="text.secondary">
                      The distribution is read-only because this test is no longer a draft
                      {resolvedCount > 0 ? ` (${resolvedCount} questions resolved).` : "."}
                    </Typography>
                  )}
                  {editable && !hasWeightage && noDistribution && (
                    <Alert severity="info">
                      {isEdit
                        ? "This test has no distribution yet: pick items below, or upload a weightage file in the Weightage section."
                        : "The distribution is optional while creating: leave it empty to create the test now and upload a weightage file for it afterwards."}
                    </Alert>
                  )}
                  {!legacyType && (
                    <TestDistributionBuilder
                      courseId={courseId}
                      testTypeId={testTypeId}
                      level={level}
                      multiChapter={Boolean(currentType?.allowMultipleChapters)}
                      singleTopic={/^practice$/i.test(currentType?.name || "")}
                      minPyqPercentage={currentType?.minPyqPercentage || 0}
                      examMonth={isPyqType ? examMonth : ""}
                      examYear={isPyqType ? examYear : ""}
                      totalQuestions={totalQuestions}
                      value={distribution}
                      onChange={setDistribution}
                      disabled={(isEdit && !isDraft) || hasWeightage}
                    />
                  )}
                </CardContent>
              </Card>

              {isEdit && (
                <Card sx={{ ...cardSx, overflow: "hidden", order: 4 }}>
                  <SectionHeader
                    icon={<VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
                    title="Preview"
                    subtitle="Summary computed from the current distribution"
                    action={
                      <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={() => loadPreview(testId)}
                        disabled={previewLoading}
                        {...BUTTON_STYLES.secondary}
                      >
                        Refresh
                      </Button>
                    }
                  />
                  <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    {previewLoading && !preview ? (
                      <Skeleton variant="rounded" height={120} />
                    ) : previewError ? (
                      <Alert severity="warning">{previewError}</Alert>
                    ) : preview ? (
                      <>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
                          <StatTile icon={<QuizOutlinedIcon fontSize="small" />} label="Total Questions" value={preview.totalQuestions} />
                          <StatTile
                            icon={<AccessTimeOutlinedIcon fontSize="small" />}
                            label="Duration"
                            value={`${preview.durationMinutes} min`}
                          />
                          <StatTile icon={<StarsOutlinedIcon fontSize="small" />} label="Total Marks" value={preview.totalMarks} />
                          <StatTile
                            icon={<RemoveCircleOutlineOutlinedIcon fontSize="small" />}
                            label="Negative Marks / Question"
                            value={preview.negativeMarksPerQuestion}
                          />
                        </Box>

                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", mb: 1, color: "#1a1a2e" }}>
                            Difficulty Distribution
                          </Typography>
                          {(preview.difficultyDistribution || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No questions configured yet.
                            </Typography>
                          ) : (
                            <>
                              <Box sx={{ display: "flex", height: 8, borderRadius: 999, overflow: "hidden", mb: 1 }}>
                                {preview.difficultyDistribution.map((d) => (
                                  <Box
                                    key={d.difficulty}
                                    sx={{ width: `${d.percentage}%`, backgroundColor: DIFFICULTY_COLORS[d.difficulty] }}
                                  />
                                ))}
                              </Box>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {preview.difficultyDistribution.map((d) => (
                                  <Chip
                                    key={d.difficulty}
                                    label={`${labelOf(TEST_DIFFICULTIES, d.difficulty)} · ${d.questionCount} (${Math.round(d.percentage)}%)`}
                                    size="small"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.68rem",
                                      color: DIFFICULTY_COLORS[d.difficulty],
                                      backgroundColor: `${DIFFICULTY_COLORS[d.difficulty]}1f`,
                                    }}
                                  />
                                ))}
                              </Stack>
                            </>
                          )}
                        </Box>

                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", mb: 1, color: "#1a1a2e" }}>
                            {DISTRIBUTION_HEADINGS[preview.distributionLevel] || "Distribution"}
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  {["Name", "Qs", "%"].map((h, i) => (
                                    <TableCell
                                      key={h}
                                      align={i === 0 ? "left" : "center"}
                                      sx={{ backgroundColor: ACCENT, color: "#fff", fontWeight: 600, fontSize: "0.7rem" }}
                                    >
                                      {h}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(preview.hierarchyDistribution || []).length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={3} sx={{ color: "text.secondary", textAlign: "center", py: 2 }}>
                                      Nothing picked yet
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  preview.hierarchyDistribution.map((row) => (
                                    <TableRow key={row.name} sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}>
                                      <TableCell sx={{ fontSize: "0.8rem" }}>{row.name}</TableCell>
                                      <TableCell align="center" sx={{ fontSize: "0.8rem" }}>
                                        {row.questionCount}
                                      </TableCell>
                                      <TableCell align="center" sx={{ fontSize: "0.8rem" }}>
                                        {Math.round(row.percentage)}%
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </>
                    ) : null}
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              ...cardSx,
              position: "sticky",
              bottom: 8,
              zIndex: 10,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              py: 1.5,
              px: 2.5,
            }}
          >
            <Button
              component={Link}
              to="/assessment"
              onClick={goBack}
              disabled={submitting || publishing}
              {...BUTTON_STYLES.secondary}
            >
              Back to Tests
            </Button>
            {isEdit && isDraft && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {dirty && (
                  <Typography variant="caption" sx={{ color: "#b26a00", fontWeight: 600 }}>
                    Save your changes before publishing
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  startIcon={<PublishOutlinedIcon />}
                  onClick={() => setPublishOpen(true)}
                  disabled={submitting || publishing || dirty || distribution.items.length === 0}
                  {...BUTTON_STYLES.secondary}
                >
                  Publish
                </Button>
              </Box>
            )}
            {(!isEdit || isDraft) && (
              <LoadingButton
                variant="contained"
                loading={submitting}
                onClick={handleSave}
                disabled={!isValid || publishing}
                {...BUTTON_STYLES.primary}
              >
                {isEdit ? "Save Changes" : "Create Test"}
              </LoadingButton>
            )}
          </Box>
        </Box>
      )}

      <ConfirmDialog
        open={publishOpen}
        title="Publish Test"
        message="Publishing picks the questions for every item in the distribution and locks the test. This can't be undone. Continue?"
        confirmText="Publish"
        loading={publishing}
        onClose={() => setPublishOpen(false)}
        onConfirm={handlePublish}
      />
    </Box>
  );
}

export default AssessmentTestForm;
